/**
 * AppSidebar — the tenant-branded collapsible sidebar (issue #7).
 *
 * Adapted from standalone-search's AppSidebar.tsx.
 * Key changes from the donor:
 *   - `imamLogo` import removed; logo comes from `logoUrl` prop (GET /tenant).
 *   - `headerTitle` default "Scholar Hub" replaced by `tenantName` prop.
 *   - `pinned` / `useSidebar().pinned` removed — not exported by LMS sidebar.tsx.
 *   - `contextualContent` / `contextualItemUrl` removed (not needed yet).
 *   - `items` is required (passed as role-resolved nav from AppShell).
 *   - Collapse toggle kept via `toggleSidebar` + `state` from useSidebar.
 *   - Closes the mobile Sheet on navigation (issue: a destination reached
 *     from inside it, e.g. the account menu, left the Sheet's modal layer
 *     open over the new route and aria-hidden the page it had just opened).
 *
 * Bento fusion: the rail is light, so the tenant's colour mark renders as
 * supplied, and the rail header carries what the desktop top bar used to: the
 * workspace context and the inbox. The top bar remains only on mobile, where
 * it holds the trigger that opens this rail. Header controls, items, and the
 * footer share one density from the control tokens; the collapsed rail keeps
 * the mark and the inbox on the icon rail's 32 px rhythm.
 */
import { useEffect } from "react";
import { Link, useLocation } from "react-router";
import { LifeBuoy, PanelLeftClose, PanelLeftOpen } from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { frameHook } from "@/components/ui/box.schema";
import { cn } from "@/lib/utils";
import { type NavGroup } from "@/navigation/sidebar-items";

import { InboxLink } from "./InboxLink";
import { NavMain } from "./NavMain";
import { NavUser } from "./NavUser";
import { RAIL_ITEM } from "./rail-item";

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  /** Role-resolved nav groups from AppShell (via sidebarItemsForRole). */
  items: NavGroup[];
  /** Display name from GET /tenant — never hardcoded. */
  tenantName: string;
  /** Logo URL from GET /tenant. Falls back to initials when null. */
  logoUrl: string | null;
  /** Optional institutional parent line from the tenant presentation layer. */
  parentOrg?: string | null;
  /** Optional top-level link for the brand mark. */
  headerHref?: string;
  /** The workspace the reader is in ("My Teaching", "Registrar"). */
  contextLabel?: string;
  /** The inbox for this workspace, with its accessible name already composed. */
  inbox?: { href: string; label: string; unread: boolean };
}

const HEADER_CONTROL =
  "flex size-[var(--control-height-compact)] shrink-0 items-center justify-center rounded-control transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)] hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring";

export function AppSidebar({
  items,
  tenantName,
  logoUrl,
  parentOrg,
  headerHref,
  contextLabel,
  inbox,
  ...props
}: AppSidebarProps) {
  const { toggleSidebar, state, isMobile, setOpenMobile } = useSidebar();
  // The drawer always opens in full: a rail collapsed on the desktop must not
  // hand a phone a header with nothing in it but the mark.
  const collapsed = state === "collapsed" && !isMobile;
  const location = useLocation();

  /**
   * Every mobile destination reached from inside this Sheet - a sidebar
   * link, the account menu's Profile/Academic record/Faculty directory -
   * navigates without dismissing it first. Radix keeps the rest of the page
   * `aria-hidden` while its modal layer stays open, so the new route renders
   * behind an inert overlay until this fires.
   */
  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [location.pathname, isMobile, setOpenMobile]);

  const CollapseIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const collapseLabel = collapsed ? "Expand sidebar" : "Collapse sidebar";

  const brandMark = logoUrl ? (
    <img
      src={logoUrl}
      alt={tenantName}
      className="size-7 shrink-0 object-contain"
    />
  ) : (
    /* Initials fallback — styled like the Folio .side-brand .mark */
    <span
      className="flex size-7 shrink-0 items-center justify-center rounded-control bg-[var(--app-shell-mark)] text-small font-semibold text-[var(--app-shell-mark-foreground)]"
    >
      {tenantName.slice(0, 2).toUpperCase()}
    </span>
  );

  // Wraps rather than truncates: an institution rendered as "Murtada Acade…"
  // in its own portal reads as a broken build.
  const brandText = (
    <span className="min-w-0">
      <span className="block text-balance text-ui font-semibold">{tenantName}</span>
      {(contextLabel ?? parentOrg) && (
        <span className="block truncate text-small text-muted-foreground">{contextLabel ?? parentOrg}</span>
      )}
    </span>
  );

  return (
    <Sidebar collapsible="icon" {...frameHook("rail")} {...props}>
      <SidebarHeader className="px-3 pt-3.5 pb-1 group-data-[collapsible=icon]:px-2">
        {collapsed ? (
          /* Collapsed rail: the mark is the expand affordance and the inbox
             keeps its place beneath it, both on the icon rail's rhythm. The
             mark yields to the expand glyph on hover and focus, so the
             control says what it does before it is pressed. */
          <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={toggleSidebar}
              title={collapseLabel}
              aria-label={collapseLabel}
              className={cn(HEADER_CONTROL, "group/expand relative size-8")}
            >
              <span className="flex transition-opacity duration-[var(--motion-fast)] ease-[var(--ease-standard)] group-hover/expand:opacity-0 group-focus-visible/expand:opacity-0">
                {brandMark}
              </span>
              <CollapseIcon
                className="absolute size-4 text-foreground opacity-0 transition-opacity duration-[var(--motion-fast)] ease-[var(--ease-standard)] group-hover/expand:opacity-100 group-focus-visible/expand:opacity-100"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </button>
            {inbox && (
              <InboxLink href={inbox.href} label={inbox.label} unread={inbox.unread} className="size-8" />
            )}
          </div>
        ) : (
          <div className="flex min-h-11 items-center gap-1 overflow-hidden">
            {headerHref ? (
              <Link
                to={headerHref}
                className="flex min-w-0 items-center gap-2.5 rounded-control p-1 transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
              >
                {brandMark}
                {brandText}
              </Link>
            ) : (
              <div className="flex min-w-0 items-center gap-2.5 p-1">
                {brandMark}
                {brandText}
              </div>
            )}

            <div className="ms-auto flex shrink-0 items-center">
              {inbox && <InboxLink href={inbox.href} label={inbox.label} unread={inbox.unread} />}
              <button
                type="button"
                onClick={toggleSidebar}
                title={collapseLabel}
                aria-label={collapseLabel}
                className={cn(HEADER_CONTROL, "text-muted-foreground hover:text-foreground")}
              >
                <CollapseIcon className="size-4" strokeWidth={1.75} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={items} />
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Feedback"
              className={cn(RAIL_ITEM, "text-muted-foreground")}
            >
              <a
                href="mailto:dev@imam-us.org"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LifeBuoy strokeWidth={1.75} aria-hidden="true" />
                <span>Support</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
